import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from './apiConfig';

const fetchWithToken = async (url, options = {}) => {
  try {
    const token = await AsyncStorage.getItem('userToken');
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      console.log(`Usando token: ${token.substring(0, 10)}...`);
    } else {
      console.log('No se encontró token de autenticación');
    }
    
    // Sucursal activa: agregar _tenant_id a TODAS las peticiones para operar
    // sobre la sucursal seleccionada (mismo comportamiento que services/api.js).
    // Sin esto, el inventario ignoraba la sucursal y mostraba el de la matriz.
    let finalUrl = url;
    try {
      const sucursalId = await AsyncStorage.getItem('activeSucursalId');
      if (sucursalId) {
        finalUrl += (url.includes('?') ? '&' : '?') + '_tenant_id=' + sucursalId;
      }
    } catch (e) { /* noop */ }

    console.log(`Realizando petición a: ${API_URL}${finalUrl}`);

    const response = await fetch(`${API_URL}${finalUrl}`, {
      ...options,
      headers,
    });
    
    // Intentamos parsear la respuesta como JSON
    let data;
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      data = await response.json();
    } else {
      const text = await response.text();
      console.log('Respuesta no JSON:', text);
      data = { message: text };
    }
    
    if (!response.ok) {
      const errorMessage = data.message || `Error ${response.status}: ${response.statusText}`;
      console.error(`Error API (${response.status}):`, errorMessage);
      throw new Error(errorMessage);
    }
    
    return data;
  } catch (error) {
    console.error('API Error:', error);
    // Mejorar los mensajes de error para problemas comunes
    if (error.message === 'Network request failed') {
      throw new Error('No se pudo conectar al servidor. Verifica tu conexión a internet y que el servidor esté en funcionamiento.');
    }
    
    throw error;
  }
};

// Exportamos un objeto con todas las funciones necesarias para la pantalla de edición de productos
const productosApi = {
  // Productos
  getProductos: async () => {
    console.log('Obteniendo lista de productos');
    try {
      const response = await fetchWithToken('/productos');
      console.log(`Se obtuvieron ${response.length} productos`);
      
      // Depurar datos de categoría
      response.forEach(producto => {
        console.log(`Producto ${producto.id} - ${producto.nombre}:`);
        console.log(`  categoria_id: ${producto.categoria_id}`);
        console.log(`  categoria_nombre: ${producto.categoria_nombre}`);
      });
      
      // Asegurarse de que cada producto tenga el nombre de categoría
      const productosConCategorias = response.map(producto => {
        // Si el producto tiene categoria_id pero no tiene categoria_nombre, buscar el nombre
        if (producto.categoria_id && !producto.categoria_nombre) {
          // Asignar un nombre de categoría basado en el ID conocido
          switch(producto.categoria_id) {
            case 1: producto.categoria_nombre = 'Bebidas'; break;
            case 2: producto.categoria_nombre = 'Snacks'; break;
            case 3: producto.categoria_nombre = 'Lácteos'; break;
            case 4: producto.categoria_nombre = 'Limpieza'; break;
            case 5: producto.categoria_nombre = 'Cuidado Personal'; break;
            case 7: producto.categoria_nombre = 'Alimentos'; break;
            default: producto.categoria_nombre = `Categoría ${producto.categoria_id}`;
          }
          console.log(`  Asignado nombre de categoría: ${producto.categoria_nombre}`);
        }
        return producto;
      });
      
      return productosConCategorias;
    } catch (error) {
      console.error('Error al obtener productos:', error);
      throw error;
    }
  },
  
  getProducto: async (id) => {
    console.log(`Obteniendo producto con ID ${id}`);
    try {
      const response = await fetchWithToken(`/productos/${id}`);
      console.log('Producto obtenido:', response);
      
      // Depurar datos de categoría
      console.log(`Producto ${response.id} - ${response.nombre}:`);
      console.log(`  categoria_id: ${response.categoria_id}`);
      console.log(`  categoria_nombre: ${response.categoria_nombre}`);
      
      // Si el producto tiene categoria_id pero no tiene categoria_nombre, asignar un nombre
      if (response.categoria_id && !response.categoria_nombre) {
        // Asignar un nombre de categoría basado en el ID conocido
        switch(response.categoria_id) {
          case 1: response.categoria_nombre = 'Bebidas'; break;
          case 2: response.categoria_nombre = 'Snacks'; break;
          case 3: response.categoria_nombre = 'Lácteos'; break;
          case 4: response.categoria_nombre = 'Limpieza'; break;
          case 5: response.categoria_nombre = 'Cuidado Personal'; break;
          case 7: response.categoria_nombre = 'Alimentos'; break;
          default: response.categoria_nombre = `Categoría ${response.categoria_id}`;
        }
        console.log(`  Asignado nombre de categoría: ${response.categoria_nombre}`);
      }
      
      return response;
    } catch (error) {
      console.error('Error al obtener producto:', error);
      throw error;
    }
  },
  
  updateProducto: async (id, productoData) => {
    console.log(`Actualizando producto ID ${id} con datos:`, JSON.stringify(productoData));
    try {
      // Asegurarse de que el ID sea un número
      const numericId = parseInt(id);
      if (isNaN(numericId)) {
        throw new Error(`ID de producto inválido: ${id}`);
      }
      
      // Realizar la petición al servidor
      const response = await fetchWithToken(`/productos/${numericId}`, {
        method: 'PUT',
        body: JSON.stringify(productoData),
      });
      
      console.log('Respuesta de actualización de producto:', response);
      return response;
    } catch (error) {
      console.error('Error al actualizar producto:', error);
      throw error;
    }
  },
  
  // Categorías
  getCategorias: async () => {
    console.log('Obteniendo lista de categorías');
    try {
      const response = await fetchWithToken('/categorias');
      console.log('Categorías obtenidas:', response);
      return response;
    } catch (error) {
      console.error('Error al obtener categorías:', error);
      throw error;
    }
  },
  
  getCategoriaById: async (id) => {
    console.log(`Obteniendo categoría con ID ${id}`);
    try {
      const response = await fetchWithToken(`/categorias/${id}`);
      console.log(`Categoría ${id} obtenida:`, response);
      return response;
    } catch (error) {
      console.error(`Error al obtener categoría ${id}:`, error);
      throw error;
    }
  },
  
  createCategoria: async (categoriaData) => {
    console.log('Creando categoría:', JSON.stringify(categoriaData));
    try {
      const response = await fetchWithToken('/categorias', {
        method: 'POST',
        body: JSON.stringify(categoriaData),
      });
      console.log('Categoría creada:', response);
      return response;
    } catch (error) {
      console.error('Error al crear categoría:', error);
      throw error;
    }
  },
  
  // Crear un nuevo producto
  createProducto: async (productoData) => {
    console.log('Creando producto:', JSON.stringify(productoData));
    try {
      const response = await fetchWithToken('/productos', {
        method: 'POST',
        body: JSON.stringify(productoData),
      });
      console.log('Producto creado:', response);
      return response;
    } catch (error) {
      console.error('Error al crear producto:', error);
      throw error;
    }
  },

  deleteProducto: async (id) => {
    console.log(`Eliminando producto ID ${id}`);
    try {
      const response = await fetchWithToken(`/productos/${id}`, {
        method: 'DELETE',
      });
      console.log('Respuesta de eliminación de producto:', response);
      return response;
    } catch (error) {
      console.error('Error al eliminar producto:', error);
      throw error;
    }
  }
};

export default productosApi;
